import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import {
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Advices (basic_advice) widget settings. The exact files_small pattern:
// composite value + sibling order key:
//   { cta: boolean, items: Record<string,boolean> }   +  basic_advice_order
// - `cta`  : on/off for the top-right "goto advices" button. On by default.
// - `items`: per-stat show/hide, pure two-state (shown/hidden), no module
//            gating (module checks live on the advices page).
// - `order`: render order of all 6 items — everything is reorderable, no
//            pinned row.
// Hidden items are gone at runtime (no show-all overflow): everything stays
// reachable on the advices page itself.

export type BasicAdviceSettingsValue = {
  cta?: boolean;
  items?: Record<string, boolean>;
};

type Props = {
  value: BasicAdviceSettingsValue;
  onChange: (v: BasicAdviceSettingsValue) => void;
  order: string[];
  onOrderChange: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.basic_advice';

export const CoachSettingsWidgetBasicAdvice: FunctionComponent<Props> = ({
  value,
  onChange,
  order,
  onOrderChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const items = value?.items || {};
  const cta = value?.cta !== false; // default on

  // Resolve items in the (draft) order, falling back to the default order
  // when the draft is empty. Tolerates drift (unknown keys ignored, missing
  // keys appended).
  const orderedItems = HelpersClientWidgets.getOrderedBasicAdviceItems(order, items);

  // Helper to update the composite value: each per-item toggle and the cta
  // toggle write back to the same `items`/`cta` shape.
  const setItems = (nextItems: Record<string, boolean>) => onChange({ ...value, items: nextItems });

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Reorder + show/hide list for the stats. Drag handle reorders; the
        switch toggles the stat's row. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.items.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1}}>
        <DragDropProvider
          onDragEnd={(event) => {
            if (event.canceled) return;
            const { source } = event.operation;
            // With useSortable, the OptimisticSortingPlugin reorders items by
            // mutating each sortable's `index` during dragover. By dragend the
            // source's new index reflects where it was dropped, so we splice it
            // from its initial index to its new index. We can't rely on
            // event.operation.target here — the plugin re-points it at source.
            if (!source || !isSortable(source)) return;
            const from = source.sortable.initialIndex;
            const to = source.sortable.index;
            if (from === to) return;

            const currentOrder = orderedItems.map((i) => i.key);
            const next = [...currentOrder];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            onOrderChange(next);
          }}
        >
          {orderedItems.map((item, index) => (
            <BasicAdviceSortableRow
              key={item.key}
              id={item.key}
              index={index}
              item={item}
              label={t(`${LABEL_BASE}.items.${item.key}`)}
              theme={theme}
              checked={item.state === 'shown'}
              onToggle={(checked) => setItems({ ...items, [item.key]: checked })}
            />
          ))}
        </DragDropProvider>
      </Paper>
    </Grid>

    {/* Widget-level toggle: the top-right goto CTA. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.basicAdviceCtaIcon
            sx={{fontSize: '1.2rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.cta`)}
            value={cta}
            greying
            onChange={(c) => onChange({ ...value, cta: c })}
          />
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

type RowProps = {
  id: string;
  index: number;
  item: any;
  label: string;
  theme: any;
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

const BasicAdviceSortableRow: FunctionComponent<RowProps> = ({id, index, item, label, theme, checked, onToggle}) => {
  const { ref, handleRef, isDragSource, isDropTarget } = useSortable({ id, index });

  const { Icon } = item;
  // shown -> primary (green), hidden -> secondary (red)
  const stateColor = checked
    ? theme.palette.primary.main
    : theme.palette.secondary.main;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 0.5,
        marginTop: 0.5,
        backgroundColor: isDropTarget ? 'rgba(151,190,107,0.12)' : 'transparent',
        opacity: isDragSource ? 0.5 : 1,
        border: '1px solid',
        borderColor: isDropTarget ? theme.palette.primary.main : 'transparent',
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out'
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: theme.palette.text.secondary
        }}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
      <Switcher
        label={label}
        value={checked}
        greying
        onChange={onToggle}
      />
    </Box>
  );
}

// Read-only status: the 6 stat icons in saved order, coloured by state
// (primary = shown, secondary = hidden), + the cta toggle icon (on/off).
type StatusProps = {
  value: BasicAdviceSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetBasicAdviceStatus: FunctionComponent<StatusProps> = ({
  value,
  order
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const items = value?.items || {};
  const cta = value?.cta !== false; // default on

  const orderedItems = HelpersClientWidgets.getOrderedBasicAdviceItems(order, items);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 1.5
    }}>
      {orderedItems.map((item) => {
        const { Icon } = item;
        const color = item.state === 'shown'
          ? theme.palette.primary.main
          : theme.palette.secondary.main;
        return <Box
          key={item.key}
          title={t(`${LABEL_BASE}.items.${item.key}`)}
        >
          <Icon sx={{ fontSize: '1.1rem', color }} />
        </Box>;
      })}
    </Box>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.cta`)}>
        <HelpersClientWidgets.basicAdviceCtaIcon
          sx={{ fontSize: '1.1rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
    </Box>
  </Box>;
}
